<template>
  <nuxt-layout>
    <transition name="speedy" appear mode="out-in">
      <CardContentForm v-if="!completed && mounted" key="submit">
        <template #header>
          <AppNomadHeader tag="h3">
            {{ $t('signin.connexion') }}
          </AppNomadHeader>
        </template>
        <Form
          :has-cancel="false"
          :label-submit="$t('signin.button.login')"
          :sticky-nav="false"
          :add-cgu="true"
          :offset-mobile-menu="false"
        >
          <template #aside>
            <div v-if="providers.length" class="social">
              <span class="social-separator">ou</span>
              <div class="social-providers">
                <ul class="social-providers-list">
                  <li
                    v-for="provider in providers"
                    :key="provider"
                    class="social-providers-list-item"
                  >
                    <ButtonSocialIcon :provider="provider" />
                  </li>
                </ul>
              </div>
            </div>
            <hr />
            <aside class="create-account">
              <h4 class="create-acount-title">
                {{ $t('signin.button.noAccount') }}
              </h4>
              <ButtonBase :to="signupRoute" stroke="l" rounded size="normal">
                {{ $t('signin.button.createAccount') }}
              </ButtonBase>
            </aside>
          </template>
        </Form>
      </CardContentForm>
    </transition>
  </nuxt-layout>
</template>
<script lang="ts" setup>
import { ErrorTypes, Eva2Error } from '~/lib/errors/Eva2Error';

import { data } from '~/assets/data/form-signin.json';

definePageMeta({ auth: false, locked: true });

const { setLoginTokens } = useAdamLogin();

const mounted = useMounted();

const { t } = useI18n();

const ERROR_LOGIN = {
  E_EXISTING_ACCOUNT_EMAIL:
    'Un compte existe déjà avec un mot de passe associé. Connecte-toi avec ton email ✨',
  E_EXISTING_ACCOUNT_GOOGLE:
    'Un compte existe déjà avec cet email. Connecte-toi avec Google ✨',
  E_EXISTING_ACCOUNT_APPLE:
    'Un compte existe déjà avec cet email. Connecte-toi avec Apple ✨',
  E_EXISTING_ACCOUNT_TIKTOK:
    'Un compte existe déjà avec cet email. Connecte-toi avec TikTok ✨',
  E_EXISTING_ACCOUNT_FACEBOOK:
    'Un compte existe déjà avec cet email. Connecte-toi avec Facebook ✨',
  E_UNKNOWN: 'Une erreur est survenue, merci de réessayer plus tard.',
};

useWebsiteHead({ title: t('title.signin') });

const route = useRoute();

const { query } = route;

const continueUri = (query.continue as string) || '/';

const queryError = (query.err as string) || null;

const { open: openDialogError } = useDialogErrorMessage();

const error = ref<Eva2Error>(null);

const config = useConfigStore();

const providers = computed<('apple' | 'google' | 'facebook' | 'tiktok')[]>(
  () => {
    const items = [];
    if (config.featureFlags.ssoAuthApple) items.push('apple');
    if (config.featureFlags.ssoAuthGoogle) items.push('google');
    if (config.featureFlags.ssoAuthFacebook) items.push('facebook');
    if (config.featureFlags.ssoAuthTiktok) items.push('tiktok');
    return items;
  }
);

const formPage = await useFormPage({
  type: FormStrategyType.SingleStep,
  config: data as FormField[],
  urlPost: '/auth/local',
  anonym: true,
  forceRefreshProfileOnComplete: false,
  checkValidatedFormOnMounted: false,
  cancelable: false,
  labelSubmit: t('signin.button.login'),
});

const { onSubmitComplete, completed } = formPage;

onSubmitComplete(async ({ error, result }) => {
  if (!error) {
    const {
      id,
      member: { token, refreshToken },
    } = result;
    await setLoginTokens({ id, token, refreshToken });
    await useAppInit(continueUri);
  }
});

provide(INJECT_FORM_PAGE, formPage);

const focused = ref(true);

const stopListeningBlur = useEventListener(window, 'blur', () => {
  focused.value = false;
});

const stopListeningFocus = useEventListener(window, 'focus', () => {
  focused.value = true;
});

const signupRoute = computed(() => {
  return {
    path: providers.value.length ? '/signup' : '/signup/email',
    query,
  };
});

watch(
  () => focused.value,
  async (to, from) => {
    if (!from && to) {
      await useAppInit((query.continue as string) || '/');
    }
  }
);

onUnmounted(() => {
  stopListeningBlur();
  stopListeningFocus();
});

onMounted(async () => {
  await useAppInit(continueUri);
  if (queryError && ERROR_LOGIN[queryError]) {
    error.value = new Eva2Error(ErrorTypes.Adam, {}, ERROR_LOGIN[queryError]);
  }
});

watch(
  () => error.value,
  (err) => {
    if (err) {
      openDialogError('Une erreur est survenue', err.message, () => {
        error.value = null;
        const queryNew = { ...query };
        delete queryNew.err;
        navigateTo(
          { path: route.path, query: queryNew },
          {
            replace: true,
          }
        );
      });
    }
  }
);
</script>
<style lang="scss" scoped>
hr {
  align-self: center;
  width: 100%;
  margin: 0.6rem 0 1.6rem;
  border: 0;
  border-bottom: 0.1rem solid var(--color_neutral_100);
}
.create-account {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap_xs);
  width: 100%;
  label {
    @include font('s', 800, 'secondary');

    color: var(--color_neutral_700);
    @include above('l') {
      @include font('m', 800, 'secondary');
    }
  }
}

.form-confirm {
  p {
    text-align: center;
  }
}

.social {
  display: flex;
  flex-direction: column;
  gap: var(--gap_s);
  width: 100%;
  &-separator {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: var(--gap_s);
    color: var(--color_neutral_600);
    @include font('s', 500, 'secondary');
    &::before,
    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 0.1rem;
      margin-top: 0.3rem;
      background-color: var(--color_neutral_100);
    }
  }
  &-providers {
    &-list {
      display: flex;
      gap: var(--gap_l);
      justify-content: center;
    }
  }
}

.create-acount-title {
  @include font('s', 800, 'secondary');
  @include above('l') {
    @include font('m', 800, 'secondary');
  }
}
</style>
